import { Link, useLocation } from 'react-router-dom'
import ListItem from '@mui/material/ListItem'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Tooltip from '@mui/material/Tooltip'
import Chip from '@mui/material/Chip'
import PermissionsCheck from 'components/PermissionsCheck'

import { LinkProps } from '../../index'

export type AppLinkProps = {
	link: LinkProps,
	onClick: () => void,
	// Drawer expanded (icon + label) vs. collapsed icon-only rail.
	open: boolean,
}

export default function AppLink( { link, onClick, open }: AppLinkProps ) {
	const { pathname } = useLocation()

	if ( link.hidden ) {
		return null
	}

	const Icon = link.icon
	// Highlight the active section; '/' must match exactly so it isn't always on.
	const selected = link.to === '/' ? pathname === '/' : pathname.startsWith( link.to )

	const listItem = (
		<ListItem disablePadding sx={{ display: 'block' }}>
			{/* On the collapsed rail the label lives in a tooltip instead. */}
			<Tooltip title={link.label} placement="right" disableHoverListener={open}>
				<ListItemButton
					component={Link}
					to={link.to}
					onClick={onClick}
					selected={selected}
					// Rounded, inset highlight for the selected/hover state.
					sx={{ justifyContent: open ? 'initial' : 'center', px: 1.5, mx: 1, borderRadius: 1 }}
				>
					<ListItemIcon sx={{ minWidth: 0, mr: open ? 2 : 'auto', justifyContent: 'center' }}>
						<Icon />
					</ListItemIcon>
					<ListItemText
						primary={link.label}
						primaryTypographyProps={{ variant: 'body2' }}
						sx={{ opacity: open ? 1 : 0 }}
					/>
					{ link.badge && open && (
						<Chip color="info" label={link.badge} sx={{ ml: 1 }} />
					) }
				</ListItemButton>
			</Tooltip>
		</ListItem>
	)

	// A link with no permissions listed is public — skip PermissionsCheck entirely
	// rather than passing it an empty array. checkPermissions treats [] the same as
	// "always allow" anyway, but usePermissions([]) still fires a real request, and
	// the backend treats an empty permissions filter as no filter at all, i.e. every
	// permission the user holds. That request runs on every render of the nav menu
	// for no reason, since a public link doesn't need permission data at all.
	if ( !link.permissions?.length ) {
		return listItem
	}

	return (
		<PermissionsCheck permissionsToCheck={link.permissions}>
			{listItem}
		</PermissionsCheck>
	)
}
